// src/context/ga4.js

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth';
import { usePermissions } from './permissions';
import { extractDateFromBigQueryTimestamp } from '../utils/ga4/helpers';
import { cacheData, getCachedData, clearCachedData } from '../utils/ga4/cacheData';
import { 
    fetchUsersFromApi,
    fetchAuditLogsFromApi,
    fetchAccountsFromApi,
    fetchPendingRequestsFromApi,
    fetchApprovedRequestsFromApi,
    fetchRejectedRequestsFromApi,
    createAccessBindingFromApi,
    removeAccessBindingFromApi,
    modifyAccessBindingFromApi,
    approvePendingRequestFromApi,
    rejectPendingRequestFromApi
} from '../utils/ga4/analyticsApi';

// Cache keys
const CACHE_KEYS = {
    USERS: 'ga4_users',
    AUDIT_LOGS: 'ga4_audit_logs',
    ACCOUNTS: 'ga4_accounts',
    PENDING_REQUESTS: 'ga4_pending_requests',
    APPROVED_REQUESTS: 'ga4_approved_requests',
    REJECTED_REQUESTS: 'ga4_rejected_requests'
};

// Create context
const Ga4Context = createContext();

// Context provider component
export const Ga4Provider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const hasGa4Permission = hasPermission('admin/ga4');

    // State for users in BigQuery
    const [users, setUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [usersError, setUsersError] = useState(null);

    // State for audit logs
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLogsLoaded, setAuditLogsLoaded] = useState(false);
    const [auditLogsError, setAuditLogsError] = useState(null);

    // State for GA4 accounts
    const [accounts, setAccounts] = useState([]);
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    // State for GA4 pending requests
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingRequestsLoaded, setPendingRequestsLoaded] = useState(false);
    const [pendingRequestsError, setPendingRequestsError] = useState(null);

    // State for GA4 approved requests
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [approvedRequestsLoaded, setApprovedRequestsLoaded] = useState(false);
    const [approvedRequestsError, setApprovedRequestsError] = useState(null);

    // State for GA4 rejected requests
    const [rejectedRequests, setRejectedRequests] = useState([]);
    const [rejectedRequestsLoaded, setRejectedRequestsLoaded] = useState(false);
    const [rejectedRequestsError, setRejectedRequestsError] = useState(null);

    // State to track context initialization
    const [initialized, setInitialized] = useState(false);

    // Refs to prevent duplicate API calls
    const isLoadingUsers = useRef(false);
    const isLoadingAuditLogs = useRef(false);
    const isLoadingAccounts = useRef(false);
    const isLoadingPendingRequests = useRef(false);
    const isLoadingApprovedRequests = useRef(false);
    const isLoadingRejectedRequests = useRef(false);
    
    // Track if initial data loading has happened
    const hasLoadedInitialData = useRef(false);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching users from BigQuery using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the users
     * @returns {Promise<Array>} Promise resolving to an array of users
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchUsers = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingUsers.current) {
            return users;
        }

        // Check if we already have the data and can return it
        if (usersLoaded && users !== null && users.length > 0 && !forceRefresh) {
            return users;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedUsers = await getCachedData(CACHE_KEYS.USERS);
            if (cachedUsers && cachedUsers.length > 0) {
                setUsers(cachedUsers);
                setUsersLoaded(true);
                setUsersError(null);
                return cachedUsers;
            }
        }
        isLoadingUsers.current = true;
        
        const startTime = performance.now();
        try {
            const userData = await fetchUsersFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GA4 Context]: Fetched ${userData.length} users in ${duration.toFixed(2)}ms`);

            // Update state
            setUsers(userData);
            setUsersLoaded(true);
            setUsersError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.USERS, userData);

            return userData;
        } catch (error) {
            if (error.response) {
                console.error(`[GA4 Context]: Error fetching users (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GA4 Context]: Error fetching users - No response received:', error.message);
            } else {
                console.error('[GA4 Context]: Error fetching users:', error.message);
            }

            setUsersError(error.response?.data?.error?.text || error.message || 'Failed to fetch users data');
            return [];
        } finally {
            isLoadingUsers.current = false;
        }
    }, [users, usersLoaded]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching audit logs using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the audit logs
     * @returns {Promise<Array>} Promise resolving to an array of audit logs
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchAuditLogs = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingAuditLogs.current) {
            return auditLogs;
        }

        // Check if we already have the data and can return it
        if (auditLogsLoaded && auditLogs !== null && !forceRefresh) {
            return auditLogs;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedLogs = await getCachedData(CACHE_KEYS.AUDIT_LOGS);
            if (cachedLogs && cachedLogs.length > 0) {
                setAuditLogs(cachedLogs);
                setAuditLogsLoaded(true);
                setAuditLogsError(null);
                return cachedLogs;
            }
        }
        isLoadingAuditLogs.current = true;

        const startTime = performance.now();
        try {
            const logsData = await fetchAuditLogsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GA4 Context]: Fetched ${logsData.length} audit logs in ${duration.toFixed(2)}ms`);

            // Update state
            setAuditLogs(logsData);
            setAuditLogsLoaded(true);
            setAuditLogsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.AUDIT_LOGS, logsData);

            return logsData;
        } catch (error) {
            if (error.response) {
                console.error(`[GA4 Context]: Error fetching audit logs (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GA4 Context]: Error fetching audit logs - No response received:', error.message);
            } else {
                console.error('[GA4 Context]: Error fetching audit logs:', error.message);
            }

            setAuditLogsError(error.response?.data?.error?.text || error.message || 'Failed to fetch audit logs');
            return [];
        } finally {
            isLoadingAuditLogs.current = false;
        }
    }, [auditLogs, auditLogsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GA4 accounts using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GA4 accounts
     * @returns {Promise<Array>} Promise resolving to an array of GA4 accounts
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchAccounts = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingAccounts.current) {
            //console.log('Ga4Context: Accounts are already being fetched');
            return accounts;
        }

        // Check if we already have the data and can return it
        if (accountsLoaded && accounts !== null && !forceRefresh) {
            //console.log('Ga4Context: Accounts already loaded, returning existing data');
            return accounts;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedAccounts = await getCachedData(CACHE_KEYS.ACCOUNTS);
            if (cachedAccounts && cachedAccounts.length > 0) {
                setAccounts(cachedAccounts);
                setAccountsLoaded(true);
                setAccountsError(null);
                return cachedAccounts;
            }
        }

        // Set loading flag
        isLoadingAccounts.current = true;
        //console.log('Ga4Context: Fetching accounts from API...');

        // Track performance
        const startTime = performance.now();

        try {
            const accountsData = await fetchAccountsFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            //console.log(`Ga4Context: Fetched ${accountsData.length} accounts in ${duration.toFixed(2)}ms`);

            // Update state
            setAccounts(accountsData);
            setAccountsLoaded(true);
            setAccountsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.ACCOUNTS, accountsData);

            return accountsData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`Ga4Context: Error fetching accounts (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('Ga4Context: Error fetching accounts - No response received:', error.message);
            } else {
                console.error('Ga4Context: Error fetching accounts:', error.message);
            }

            setAccountsError(error.response?.data?.error?.text || error.message || 'Failed to fetch accounts');
            return [];
        } finally {
            isLoadingAccounts.current = false;
        }
    }, [accounts, accountsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching pending requests using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the pending requests
     * @returns {Promise<Array>} Promise resolving to an array of pending requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchPendingRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingPendingRequests.current) {
            return pendingRequests;
        }

        // Check if we already have the data and can return it
        if (pendingRequestsLoaded && pendingRequests !== null && !forceRefresh) {
            return pendingRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.PENDING_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setPendingRequests(cachedRequests);
                setPendingRequestsLoaded(true);
                setPendingRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingPendingRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchPendingRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GA4 Context]: Fetched ${requestsData.length} pending requests in ${duration.toFixed(2)}ms`);

            // Update state
            setPendingRequests(requestsData);
            setPendingRequestsLoaded(true);
            setPendingRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.PENDING_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GA4 Context]: Error fetching pending requests:', error);
            setPendingRequestsError(error.message || 'Failed to fetch pending requests');
            return [];
        } finally {
            isLoadingPendingRequests.current = false;
        }
    }, [pendingRequests, pendingRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching approved requests using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the approved requests
     * @returns {Promise<Array>} Promise resolving to an array of approved requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchApprovedRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingApprovedRequests.current) {
            return approvedRequests;
        }

        // Check if we already have the data and can return it
        if (approvedRequestsLoaded && approvedRequests !== null && !forceRefresh) {
            return approvedRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.APPROVED_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setApprovedRequests(cachedRequests);
                setApprovedRequestsLoaded(true);
                setApprovedRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingApprovedRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchApprovedRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GA4 Context]: Fetched ${requestsData.length} approved requests in ${duration.toFixed(2)}ms`);

            // Update state
            setApprovedRequests(requestsData);
            setApprovedRequestsLoaded(true);
            setApprovedRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.APPROVED_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GA4 Context]: Error fetching approved requests:', error);
            setApprovedRequestsError(error.message || 'Failed to fetch approved requests');
            return [];
        } finally {
            isLoadingApprovedRequests.current = false;
        }
    }, [approvedRequests, approvedRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching rejected requests using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the rejected requests
     * @returns {Promise<Array>} Promise resolving to an array of rejected requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchRejectedRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingRejectedRequests.current) {
            //console.log('Ga4Context: Rejected requests are already being fetched');
            return rejectedRequests;
        }

        // Check if we already have the data and can return it
        if (rejectedRequestsLoaded && rejectedRequests !== null && !forceRefresh) {
            //console.log('Ga4Context: Rejected requests already loaded, returning existing data:', rejectedRequests.length, 'requests');
            return rejectedRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.REJECTED_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setRejectedRequests(cachedRequests);
                setRejectedRequestsLoaded(true);
                setRejectedRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingRejectedRequests.current = true;
        
        const startTime = performance.now();
        try {
            const requestsData = await fetchRejectedRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GA4 Context]: Fetched ${requestsData.length} rejected requests in ${duration.toFixed(2)}ms`);

            // Update state
            setRejectedRequests(requestsData);
            setRejectedRequestsLoaded(true);
            setRejectedRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.REJECTED_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GA4 Context]: Error fetching rejected requests:', error);
            setRejectedRequestsError(error.message || 'Failed to fetch rejected requests');
            return [];
        } finally {
            isLoadingRejectedRequests.current = false;
        }
    }, [rejectedRequests, rejectedRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for refreshing all data at once using the utils GA4 API service
     * @param {boolean} forceRefresh - Whether to force a refresh of all data
     * @returns {Promise<boolean>} Promise resolving to a boolean indicating if the refresh was successful
     */
    /* -------------------------------------------------------------------------------------------- */
    const refreshAll = useCallback(async (forceRefresh = true) => {
        // Track performance
        const startTime = performance.now();

        // Run all data refreshes in parallel for better performance
        const promises = [
            fetchUsers(forceRefresh),
            fetchAuditLogs(forceRefresh),
            fetchAccounts(forceRefresh),
            fetchPendingRequests(forceRefresh),
            fetchApprovedRequests(forceRefresh),
            fetchRejectedRequests(forceRefresh)
        ];

        // Wait for all to complete
        const results = await Promise.all(promises);

        const duration = performance.now() - startTime;
        console.log(`[GA4 Context]: Refreshed all GA4 data in ${duration.toFixed(2)}ms`);

        return true;
    }, [fetchUsers, fetchAuditLogs, fetchAccounts, fetchPendingRequests, fetchApprovedRequests, fetchRejectedRequests]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper & useEffect for eager loading data when user is authenticated
     * @returns {void}
     */
    /* -------------------------------------------------------------------------------------------- */
    useEffect(() => {
        // Skip if no user or already loaded
        if (!isAuthenticated || !user?.email || hasLoadedInitialData.current) {
            return;
        }

        // Wait for permissions to load, then check if user has ga4 admin access
        if (permissionsLoading) {
            console.log('[GA4 Context]: Waiting for permissions to load...');
            return;
        }

        // If user doesn't have ga4 admin permission, skip all data fetching
        if (!hasGa4Permission) {
            console.log('[GA4 Context]: User lacks admin/ga4 permission, skipping data fetch');

            // Set flag to prevent re-triggering
            hasLoadedInitialData.current = true;

            // Mark all data as "loaded" with empty values to prevent loading spinners
            setUsersLoaded(true);
            setAuditLogsLoaded(true);
            setAccountsLoaded(true);
            setPendingRequestsLoaded(true);
            setApprovedRequestsLoaded(true);
            setRejectedRequestsLoaded(true);

            // Exit early so no API calls will be made
            return;
        }

        const loadInitialData = async () => {
            try {
                // Set flag to prevent multiple loads
                hasLoadedInitialData.current = true;

                // Track performance
                const startTime = performance.now();

                // Log which data types we're fetching
                //console.log('Ga4Context: Starting API data fetch for all data types');

                // Check if we have any cached data first
                const cachedUsers = await getCachedData(CACHE_KEYS.USERS);
                const cachedAuditLogs = await getCachedData(CACHE_KEYS.AUDIT_LOGS);
                const cachedAccounts = await getCachedData(CACHE_KEYS.ACCOUNTS);
                const cachedPendingRequests = await getCachedData(CACHE_KEYS.PENDING_REQUESTS);
                const cachedApprovedRequests = await getCachedData(CACHE_KEYS.APPROVED_REQUESTS);
                const cachedRejectedRequests = await getCachedData(CACHE_KEYS.REJECTED_REQUESTS);

                // Set any cached data we find to prevent flash of loading state
                if (cachedUsers?.length > 0) {
                    setUsers(cachedUsers);
                    setUsersLoaded(true);
                }

                if (cachedAuditLogs?.length > 0) {
                    setAuditLogs(cachedAuditLogs);
                    setAuditLogsLoaded(true);
                }

                if (cachedAccounts?.length > 0) {
                    setAccounts(cachedAccounts);
                    setAccountsLoaded(true);
                }

                if (cachedPendingRequests?.length !== undefined) { // Accept empty arrays
                    setPendingRequests(cachedPendingRequests || []);
                    setPendingRequestsLoaded(true);
                }

                if (cachedApprovedRequests?.length !== undefined) { // Accept empty arrays
                    setApprovedRequests(cachedApprovedRequests || []);
                    setApprovedRequestsLoaded(true);
                }

                if (cachedRejectedRequests?.length !== undefined) { // Accept empty arrays
                    setRejectedRequests(cachedRejectedRequests || []);
                    setRejectedRequestsLoaded(true);
                }

                // Always fetch fresh data in the background, regardless of cache
                // Create individual timeouts for each data type to prevent blocking on slow endpoints
                const loadWithTimeout = async (fetchFn, dataType, timeoutMs = 90000) => {
                    try {
                        // Create a timeout promise that won't cause unhandled promise rejections
                        const timeoutPromise = new Promise((_, reject) => {
                            const timeoutId = setTimeout(() => {
                                clearTimeout(timeoutId);
                                console.warn(`[GA4 Context]: ${dataType} data loading approaching timeout (${timeoutMs/1000}s) - will continue in background`);
                                reject({ isTimeout: true, message: `Timeout reached for ${dataType}` });
                            }, timeoutMs);
                        });

                        try {
                            // Race the actual fetch against the timeout
                            const result = await Promise.race([fetchFn(true), timeoutPromise]);
                            return result;
                        } catch (err) {
                            // Suppress timeout errors to prevent console errors
                            if (err?.isTimeout) {
                                return null;
                            }
                            // Pass through other errors
                            throw err;
                        }
                    } catch (err) {
                        console.error(`[GA4 Context]: Error loading ${dataType} data:`, err);
                        return null;
                    }
                };

                // Load all data types in parallel
                const promises = [
                    loadWithTimeout(fetchUsers, 'users'),
                    loadWithTimeout(fetchAuditLogs, 'auditLogs'),
                    loadWithTimeout(fetchAccounts, 'accounts'),
                    loadWithTimeout(fetchPendingRequests, 'pendingRequests'),
                    loadWithTimeout(fetchApprovedRequests, 'approvedRequests'),
                    loadWithTimeout(fetchRejectedRequests, 'rejectedRequests')
                ];

                // Wait for all data types to either load or timeout
                const results = await Promise.allSettled(promises);

                // For pending requests, even if the array is empty, consider it loaded
                if (results[3].status === 'fulfilled' && results[3].value !== null) {
                    setPendingRequests(results[3].value || []);
                    setPendingRequestsLoaded(true);
                }

                // For approved requests, even if the array is empty, consider it loaded
                if (results[4].status === 'fulfilled' && results[4].value !== null) {
                    setApprovedRequests(results[4].value || []);
                    setApprovedRequestsLoaded(true);
                }

                // For rejected requests, even if the array is empty, consider it loaded
                if (results[5].status === 'fulfilled' && results[5].value !== null) {
                    setRejectedRequests(results[5].value || []);
                    setRejectedRequestsLoaded(true);
                }

                // Log any failed requests
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        const dataType = ['users', 'auditLogs', 'accounts', 'pendingRequests', 'approvedRequests', 'rejectedRequests'][index];
                        console.error(`Ga4Context: Failed to fetch ${dataType} data:`, result.reason);
                    }
                });

                // Log performance
                const duration = performance.now() - startTime;
                console.log(`Ga4Context: Initial data loading complete in ${duration.toFixed(2)}ms`);
            } catch (err) {
                console.error('Ga4Context: Error during initial data loading:', err);
            }
        };

        // Start loading data
        loadInitialData();
    }, [isAuthenticated, user?.email, permissionsLoading, hasGa4Permission]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper & separate useEffect for marking context as initialized 
     * when auth state is available. This allows components to know when they can 
     * safely use the context.
     * @param {boolean} initialized - Whether the context is initialized
     * @param {boolean} isAuthenticated - Whether the user is authenticated
     * @returns {void}
     */
    /* -------------------------------------------------------------------------------------------- */
    useEffect(() => {
        if (!initialized && isAuthenticated !== null) {
            console.log('Ga4Context: Context initialized with auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
            setInitialized(true);
        }
    }, [isAuthenticated, initialized]);

    // Prepare context value
    const contextValue = {
        // User data
        users,
        usersLoaded,
        usersError,

        // Audit logs
        auditLogs,
        auditLogsLoaded,
        auditLogsError,

        // GA4 accounts
        accounts,
        accountsLoaded,
        accountsError,

        // Pending requests
        pendingRequests,
        pendingRequestsLoaded,
        pendingRequestsError,

        // Approved requests
        approvedRequests,
        approvedRequestsLoaded,
        approvedRequestsError,

        // Rejected requests
        rejectedRequests,
        rejectedRequestsLoaded,
        rejectedRequestsError,

        // Actions
        fetchUsers,
        fetchAuditLogs,
        fetchAccounts,
        fetchPendingRequests,
        fetchApprovedRequests,
        fetchRejectedRequests,
        refreshAll,

        // Access operations (to be called from UI)
        createAccessBindingFromApi,
        removeAccessBindingFromApi,
        modifyAccessBindingFromApi,
        approvePendingRequestFromApi,
        rejectPendingRequestFromApi,

        // Context state
        initialized
    };

    // Render provider with value
    return (
        <Ga4Context.Provider value={contextValue}>
            {children}
        </Ga4Context.Provider>
    );
};

// Custom hook to use the GA4 context
export const useGa4 = () => {
    const context = useContext(Ga4Context);
    if (!context) {
        throw new Error('useGa4 must be used within a Ga4Provider');
    }
    return context;
};

export default Ga4Context;
